import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProjetProvider } from './context/ProjetContext';
import ConsentementGate from './components/ConsentementGate';
import ChoixDemarrageGate from './components/ChoixDemarrageGate';
import NiveauAgronomeGate from './components/NiveauAgronomeGate';

// Chaque page part dans son propre chunk JS, chargé seulement quand on y
// navigue — avant ça, tout (Dashboard, GestionProjet, AdminPlateforme...)
// partait dans un seul bundle de ~305 kB téléchargé même par un investisseur
// qui n'a besoin que de son propre tableau de bord.
const Login = lazy(() => import('./pages/Login'));
const Vitrine = lazy(() => import('./pages/Vitrine'));
const Projets = lazy(() => import('./pages/Projets'));
const Investir = lazy(() => import('./pages/Investir'));
const Contact = lazy(() => import('./pages/Contact'));
const CommentCaMarche = lazy(() => import('./pages/CommentCaMarche'));
const AdminVitrine = lazy(() => import('./pages/AdminVitrine'));
const MotDePasseOublie = lazy(() => import('./pages/MotDePasseOublie'));
const ReinitialiserMotDePasse = lazy(() => import('./pages/ReinitialiserMotDePasse'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Elevage = lazy(() => import('./pages/Elevage'));
const Reproduction = lazy(() => import('./pages/Reproduction'));
const Generations = lazy(() => import('./pages/Generations'));
const Liquidation = lazy(() => import('./pages/Liquidation'));
const Gestion = lazy(() => import('./pages/Gestion'));
const Commerce = lazy(() => import('./pages/Commerce'));
const Analyses = lazy(() => import('./pages/Analyses'));
const Rapport = lazy(() => import('./pages/Rapport'));
const Profil = lazy(() => import('./pages/Profil'));
const Investissement = lazy(() => import('./pages/Investissement'));
const InvestisseurDashboard = lazy(() => import('./pages/InvestisseurDashboard'));
const GestionProjet = lazy(() => import('./pages/GestionProjet'));
const Ferme = lazy(() => import('./pages/Ferme'));
const GestionFerme = lazy(() => import('./pages/GestionFerme'));
const ApercuFerme = lazy(() => import('./pages/ApercuFerme'));
const ApercuFermeProjet = lazy(() => import('./pages/ApercuFermeProjet'));
const GestionAcces = lazy(() => import('./pages/GestionAcces'));
const ApercuPublic = lazy(() => import('./pages/ApercuPublic'));
const ApercuPublicProjet = lazy(() => import('./pages/ApercuPublicProjet'));
const GestionLiensPublics = lazy(() => import('./pages/GestionLiensPublics'));
const GestionProfils = lazy(() => import('./pages/GestionProfils'));
const Caisses = lazy(() => import('./pages/Caisses'));
const Bilan = lazy(() => import('./pages/Bilan'));
const Equipements = lazy(() => import('./pages/Equipements'));
const Journal = lazy(() => import('./pages/Journal'));
const AdminPlateforme = lazy(() => import('./pages/AdminPlateforme'));
const PolitiqueConfidentialite = lazy(() => import('./pages/PolitiqueConfidentialite'));
const DemarrageRattrapage = lazy(() => import('./pages/DemarrageRattrapage'));
const NouveauProjet = lazy(() => import('./pages/NouveauProjet'));
const MesProjets = lazy(() => import('./pages/MesProjets'));
const ProjetsAVenir = lazy(() => import('./pages/ProjetsAVenir'));
const NouveauProjetAVenir = lazy(() => import('./pages/NouveauProjetAVenir'));
const DashboardTechnicien = lazy(() => import('./pages/DashboardTechnicien'));
const VenteTechnicien = lazy(() => import('./pages/VenteTechnicien'));
const RattrapageFerme = lazy(() => import('./pages/RattrapageFerme'));
const OnboardingStagiaire = lazy(() => import('./pages/OnboardingStagiaire'));
const MonParcours = lazy(() => import('./pages/MonParcours'));

const ChargementPage = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-gray-500 text-sm">Chargement...</div>
  </div>
);

const ProtectedRoute = ({ children, roles, skipOnboardingGate }) => {
  const { utilisateur, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500 text-sm">Chargement...</div>
      </div>
    );
  }
  if (!utilisateur) return <Navigate to="/login" />;
  // La capacité composable est_investisseur (migration_016) donne accès aux
  // routes ouvertes au rôle 'investisseur' à n'importe quel autre rôle qui
  // l'a en plus du sien — en complément du check de rôle existant, jamais à
  // la place (un vrai 'investisseur' reste couvert même si le flag était
  // faux).
  const accesParCapaciteInvestisseur = roles && roles.includes('investisseur') && utilisateur.est_investisseur === true;
  if (roles && !roles.includes(utilisateur.role) && !accesParCapaciteInvestisseur) return <Navigate to="/login" />;
  // Un stagiaire dont l'onboarding (consentement + questionnaire) n'est pas
  // terminé est bloqué sur cet écran avant d'accéder au reste de l'appli —
  // sauf sur la page d'onboarding elle-même, sans quoi elle se redirigerait
  // vers elle-même en boucle.
  if (!skipOnboardingGate && utilisateur.role === 'stagiaire' && utilisateur.onboarding_complete === false) {
    return <Navigate to="/onboarding-stagiaire" />;
  }
  return <ConsentementGate><ChoixDemarrageGate><NiveauAgronomeGate>{children}</NiveauAgronomeGate></ChoixDemarrageGate></ConsentementGate>;
};

const RedirectByRole = () => {
  const hostname = window.location.hostname;
  const { utilisateur } = useAuth();
  if (hostname.startsWith("admin.")) return <Navigate to="/admin-plateforme" />;
  if (hostname === "lagrandefermeparticipative.com" || hostname === "www.lagrandefermeparticipative.com") return <Vitrine />;
  if (!utilisateur) return <Navigate to="/login" />;
  if (utilisateur.role === 'gestionnaire') return <Navigate to="/dashboard" />;
  if (utilisateur.role === 'gestion_invest') return <Navigate to="/dashboard" />;
  if (utilisateur.role === 'technicien') return <Navigate to="/dashboard-technicien" />;
  if (utilisateur.role === 'tech_invest') return <Navigate to="/dashboard-technicien" />;
  if (utilisateur.role === 'stagiaire') return <Navigate to="/mon-parcours" />;
  return <Navigate to="/investissement" />;
};

function App() {
  const estAdmin = window.location.hostname.startsWith('admin.');
  return (
    <AuthProvider>
      <Router>
        <ProjetProvider>
        <Suspense fallback={<ChargementPage />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
          <Route path="/reinitialiser-mot-de-passe/:token" element={<ReinitialiserMotDePasse />} />
          <Route path="/confidentialite" element={<PolitiqueConfidentialite />} />
          <Route path="/" element={<RedirectByRole />} />
          <Route path="/projets" element={<Projets />} />
          <Route path="/investir" element={<Investir />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/comment-ca-marche" element={<CommentCaMarche />} />
          <Route path="/dashboard" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/elevage" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Elevage />
            </ProtectedRoute>
          } />
          <Route path="/reproduction" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Reproduction />
            </ProtectedRoute>
          } />
          <Route path="/generations" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Generations />
            </ProtectedRoute>
          } />
          <Route path="/liquidation/:projetId" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Liquidation />
            </ProtectedRoute>
          } />
          <Route path="/gestion" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Gestion />
            </ProtectedRoute>
          } />
          <Route path="/commerce" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Commerce />
            </ProtectedRoute>
          } />
          <Route path="/analyses" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Analyses />
            </ProtectedRoute>
          } />
          <Route path="/rapport" element={
            <ProtectedRoute roles={['technicien', 'gestionnaire', 'gestion_invest', 'tech_invest', 'stagiaire']}>
              <Rapport />
            </ProtectedRoute>
          } />
          <Route path="/investissement" element={
            <ProtectedRoute roles={['investisseur', 'gestionnaire', 'gestion_invest', 'tech_invest']}>
              <Investissement />
            </ProtectedRoute>
          } />
          <Route path="/mes-investissements" element={
            <ProtectedRoute roles={['investisseur', 'gestionnaire', 'gestion_invest', 'tech_invest']}>
              <InvestisseurDashboard />
            </ProtectedRoute>
          } />
          <Route path="/profil" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest', 'technicien', 'investisseur', 'tech_invest', 'stagiaire']}>
              <Profil />
            </ProtectedRoute>
          } />
          <Route path="/projet/:projetId/gestion" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <GestionProjet />
            </ProtectedRoute>
          } />
          <Route path="/ferme" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Ferme />
            </ProtectedRoute>
          } />
          <Route path="/apercu-ferme" element={
            <ProtectedRoute>
              <ApercuFerme />
            </ProtectedRoute>
          } />
          <Route path="/apercu-ferme/:projetId" element={
            <ProtectedRoute>
              <ApercuFermeProjet />
            </ProtectedRoute>
          } />
          <Route path="/gestion-acces" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <GestionAcces />
            </ProtectedRoute>
          } />
          <Route path="/gestion-liens-publics" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <GestionLiensPublics />
            </ProtectedRoute>
          } />
          <Route path="/gestion-profils" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <GestionProfils />
            </ProtectedRoute>
          } />
          {/* Public — sans connexion, protégé uniquement par le token secret dans l'URL. */}
          <Route path="/apercu-public/:token" element={<ApercuPublic />} />
          <Route path="/apercu-public/:token/projet/:projetId" element={<ApercuPublicProjet />} />
          <Route path="/ferme/depenses" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <GestionFerme />
            </ProtectedRoute>
          } />
          <Route path="/ferme/caisses" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Caisses />
            </ProtectedRoute>
          } />
          <Route path="/bilan" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Bilan />
            </ProtectedRoute>
          } />
          <Route path="/ferme/equipements" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Equipements />
            </ProtectedRoute>
          } />
          <Route path="/journal" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <Journal />
            </ProtectedRoute>
          } />
          <Route path="/demarrage-rattrapage" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <DemarrageRattrapage />
            </ProtectedRoute>
          } />
                    <Route path="/nouveau-projet" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <NouveauProjet />
            </ProtectedRoute>
          } />
          <Route path="/mes-projets" element={
            <ProtectedRoute roles={["technicien", "tech_invest", "investisseur"]}>
              <MesProjets />
            </ProtectedRoute>
          } />
          <Route path="/projets-a-venir" element={
            <ProtectedRoute roles={["technicien", "tech_invest", "investisseur", "gestionnaire", "gestion_invest"]}>
              <ProjetsAVenir />
            </ProtectedRoute>
          } />
          <Route path="/nouveau-projet-a-venir" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <NouveauProjetAVenir />
            </ProtectedRoute>
          } />
          <Route path="/dashboard-technicien" element={
            <ProtectedRoute roles={["technicien", "tech_invest"]}>
              <DashboardTechnicien />
            </ProtectedRoute>
          } />
          <Route path="/vente-technicien" element={
            <ProtectedRoute roles={["technicien", "tech_invest"]}>
              <VenteTechnicien />
            </ProtectedRoute>
          } />
          <Route path="/onboarding-stagiaire" element={
            <ProtectedRoute roles={['stagiaire']} skipOnboardingGate>
              <OnboardingStagiaire />
            </ProtectedRoute>
          } />
          <Route path="/mon-parcours" element={
            <ProtectedRoute roles={['stagiaire', 'technicien', 'tech_invest']}>
              <MonParcours />
            </ProtectedRoute>
          } />
          <Route path="/rattrapage-ferme" element={
            <ProtectedRoute roles={['gestionnaire', 'gestion_invest']}>
              <RattrapageFerme />
            </ProtectedRoute>
          } />
          <Route path="/admin-vitrine" element={
            <ProtectedRoute>
              <AdminVitrine />
            </ProtectedRoute>
          } />
          <Route path="/admin-plateforme" element={
            <ProtectedRoute>
              <AdminPlateforme />
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
        </Suspense>
        </ProjetProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;